import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  ITabOption,
  Preloader,
  Tabs,
  InfoBlock as InfoBlockKit,
  Separator,
} from '@smartshell/ui-kit';

import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import {
  TNormalizedItem,
  ETypeSlice,
  EVariantSlice,
  ISummaryData,
} from '../types';
import { formatMoney, formatPercents, formatSeconds } from '../utils';
import { Chart } from './Chart';
import s from './ChartBlock.module.scss';
import { InfoBlock } from './InfoBlock';

interface IChartBlockProps {
  data: Array<TNormalizedItem>;
  summaryData: ISummaryData;
  type: ETypeSlice;
  setType: Dispatch<SetStateAction<ETypeSlice>>;
  loading: boolean;
  called: boolean;
}
export const ChartBlock: FC<IChartBlockProps> = ({
  data,
  summaryData,
  loading,
  called,
  type,
  setType,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const [variant, setVariant] = useState<EVariantSlice>(EVariantSlice.PROCEEDS);

  const { TYPE_OPTIONS, VARIANT_OPTIONS } = useMemo(() => {
    const TYPE_OPTIONS: Array<ITabOption<ETypeSlice>> = [
      { value: ETypeSlice.HOSTS, title: t('report.hosts.type.hosts') },
      { value: ETypeSlice.GROUPS, title: t('report.hosts.type.groups') },
    ];
    const VARIANT_OPTIONS: Array<ITabOption<EVariantSlice>> = [
      {
        value: EVariantSlice.PROCEEDS,
        title: t('report.hosts.variant.proceeds'),
      },
      { value: EVariantSlice.TIME, title: t('report.hosts.variant.time') },
    ];

    return { TYPE_OPTIONS, VARIANT_OPTIONS };
  }, [t]);

  const size = useWindowResize();

  return (
    <div className={s.container}>
      <div className={s.top}>
        <div className={s.header}>
          <div className={s.controls}>
            <Tabs
              options={TYPE_OPTIONS}
              onChange={setType}
              value={type}
              disabled={loading}
              className={s.type}
            />
            {size.width > 960 && (
              <Tabs
                options={VARIANT_OPTIONS}
                onChange={setVariant}
                value={variant}
                disabled={loading}
                className={s.variant}
              />
            )}
          </div>
        </div>
        <div className={s.info}>
          <InfoBlock
            title={t('report.hosts.total.hours')}
            data={formatSeconds(t, summaryData.totalTime, true)}
            showPlaceholder={!called || loading || data.length === 0}
          />
          {size.width < 960 && <Separator />}
          <InfoBlock
            title={t('report.hosts.total.occupation')}
            data={formatSeconds(t, summaryData.occupationTime)}
            secondaryData={formatPercents(summaryData.occupationPercentage)}
            showPlaceholder={!called || loading || data.length === 0}
          />
          {size.width < 960 && <Separator />}

          <InfoBlock
            title={t('report.hosts.total.sessions')}
            data={`${summaryData.sessionsCount} ${t('pcs')}`}
            showPlaceholder={!called || loading || data.length === 0}
          />
          {size.width < 960 && <Separator />}

          <InfoBlock
            title={t('report.hosts.average.sessions')}
            data={formatSeconds(t, summaryData.averageSessionsTime)}
            showPlaceholder={!called || loading || data.length === 0}
          />
          {size.width < 960 && <Separator />}

          <InfoBlock
            title={t('report.hosts.total.proceeds')}
            data={formatMoney(summaryData.money, currency)}
            showPlaceholder={!called || loading || data.length === 0}
          />
        </div>
      </div>
      {size.width > 960 ? (
        <Preloader
          loading={loading}
          hasData={called && data.length > 0}
          noDataPlaceholder={t(
            called ? 'report.placeholder' : 'report.placeholder.emptyDates'
          )}
        >
          <Chart data={data} variant={variant} type={type} />
        </Preloader>
      ) : (
        <InfoBlockKit
          className={s.mobile_info}
          title={t('mobile.placeholder')}
          iconName="ComputerIcon"
          color="blue"
        />
      )}
    </div>
  );
};
